import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaExclamationCircle } from 'react-icons/fa';
import { getCurrentRole } from '../utils/medicineHelpers';
import { useMedicineProviders } from '../hooks/useMedicineProviders';
import { useMedicineSearch } from '../hooks/useMedicineSearch';
import { useMedicineOrder } from '../hooks/useMedicineOrder';
import { useMedicineCart } from '../hooks/useMedicineCart';
import { useBookingPaymentLink } from '../hooks/useBookingPaymentLink';
import ProviderSelector from '../components/medicine/ProviderSelector';
import MedicineSearch from '../components/medicine/MedicineSearch';
import MedicineCartItem from '../components/medicine/MedicineCartItem';
import CartSummary from '../components/medicine/CartSummary';
import OrderActions from '../components/medicine/OrderActions';
import OrderNoteModal from '../components/medicine/OrderNoteModal';
import OrderNoteCard from '../components/medicine/OrderNoteCard';
import CancelOrderModal from '../components/medicine/CancelOrderModal';
import PrescriptionAttachments from '../components/medicine/PrescriptionAttachments';
import DeliveryAddressSelector from '../components/medicine/DeliveryAddressSelector';
import NotificationToast from '../components/common/NotificationToast';
import PaymentLinkCard from '../components/common/PaymentLinkCard';

const MedicineTransaction = ({ applicationId, isModal = false, onClose, walletTransactionId = null }) => {
  const { id: idFromParams } = useParams();
  const id = applicationId || idFromParams;
  const navigate = useNavigate();
  
  const currentRole = getCurrentRole();
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Provider state
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [medicineServiceProvider, setMedicineServiceProvider] = useState('');
  const [providerSearchText, setProviderSearchText] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  
  // Medicine search state
  const [showMedicineDropdown, setShowMedicineDropdown] = useState(false);
  
  // Modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Custom hooks
  const { 
    providers: medicineProviders, 
    filteredProviders: filteredMedicineProviders,
    loading: loadingMedicineProviders,
    error: providerError,
    filterProviders,
    providersFetchedRef
  } = useMedicineProviders(currentRole);
  
  const {
    orderId,
    orderStatus,
    orderDeliveryCharge,
    orderDeliveryChargeError,
    handlingPackagingCharge,
    handlingPackagingChargeError,
    platformCharge,
    platformChargeError,
    noteText,
    savedNote,
    vendorId,
    serviceProvider,
    orderAddressId,
    selectedAddressId,
    setSelectedAddressId,
    isPaymentLocked,
    isPaid,
    prescriptions,
    savingOrder,
    submittingOrder,
    markingAllOutOfStock,
    updatingNote,
    cancellingOrder,
    setOrderId,
    setNoteText,
    setSavedNote,
    createOrder,
    loadPrefilledCart,
    saveOrder,
    submitOrder,
    updateNote,
    cancelOrderRequest,
    markAllOutOfStock,
    updateDeliveryCharge,
    updateHandlingPackagingCharge,
    updatePlatformCharge
  } = useMedicineOrder(currentRole, id, isModal, walletTransactionId, medicineProviders);

  const {
    cart,
    cartCashback,
    setCart,
    setCartCashback,
    addMedicineToCart,
    updateCartQuantity,
    updateCartMRP,
    updateCartAyushPayPrice,
    toggleOutOfStock,
    removeFromCart,
    batchUpdateCartItems,
    refreshCart
  } = useMedicineCart(currentRole, orderId, id, walletTransactionId, isModal, updateDeliveryCharge);

  // Medicine search with vendor_id priority support
  const {
    medicines: filteredMedicines,
    loading: loadingMedicines,
    error: medicineError,
    searchText: medicineSearchText,
    setSearchText: setMedicineSearchText,
    triggerSearch: triggerMedicineSearch,
    clearMedicines: clearMedicineResults,
    hasSearched: medicineHasSearched
  } = useMedicineSearch(currentRole, orderId, vendorId, serviceProvider, selectedVendorId);

  useEffect(() => setMounted(true), []);

  // Hide dropdown when search text is cleared
  useEffect(() => {
    if (medicineSearchText.trim().length === 0) {
      setShowMedicineDropdown(false);
    }
  }, [medicineSearchText]);

  // Show dropdown when search has been executed and there are results
  useEffect(() => {
    if (medicineHasSearched && medicineSearchText.trim().length > 0) {
      // Show dropdown if there are medicines or if search completed with no results
      if (filteredMedicines.length > 0 || (!loadingMedicines && filteredMedicines.length === 0)) {
        setShowMedicineDropdown(true);
      }
    }
  }, [medicineHasSearched, filteredMedicines, loadingMedicines, medicineSearchText]);

  // Sync selectedVendorId with vendorId from order (when available)
  // This ensures catalog search uses the correct vendor_id from order response
  // which matches catalog items, not provider.id from provider list
  useEffect(() => {
    if (vendorId) {
      setSelectedVendorId(vendorId);
    }
  }, [vendorId]);

  // Show notification helper
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  }, []);

  // Load prefilled cart when opened as modal
  useEffect(() => {
    if (isModal && providersFetchedRef.current && medicineProviders.length > 0) {
      loadPrefilledCart(
        setCart,
        setCartCashback,
        setSelectedVendorId,
        setProviderSearchText,
        setMedicineServiceProvider,
        showNotification
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModal, medicineProviders.length, loadPrefilledCart, setCart, setCartCashback, showNotification]);

  // Filter providers based on search
  useEffect(() => {
    filterProviders(providerSearchText);
  }, [providerSearchText, medicineProviders, filterProviders]);

  // Handle provider selection
  const handleProviderSelect = useCallback(async (provider) => {
    if (!provider) {
      // Clear selection when user starts typing/editing
      setMedicineServiceProvider('');
      setSelectedVendorId(null);
      if (!isModal) {
        setOrderId(null);
              setCart([]);
        updateDeliveryCharge(null);
                setNoteText('');
                setSavedNote('');
              }
            return;
          }
          
    setMedicineServiceProvider(provider.id);
    setProviderSearchText(provider.name);
    setShowProviderDropdown(false);
    // Set selectedVendorId immediately for catalog search to work
    // provider.id is the correct vendor_id from provider list API
                setSelectedVendorId(provider.id);

    if (!isModal) {
      try {
        // Pass vendor_id (provider.id) and provider name
        // provider.id is the correct vendor_id that matches catalog items
        const newOrderId = await createOrder(provider.id, provider.name);
        if (newOrderId) {
          setOrderId(newOrderId);
          setCart([]);
          updateDeliveryCharge(null);
          setNoteText('');
          setSavedNote('');
          // selectedVendorId will be automatically synced to vendorId from order response via useEffect
        }
      } catch (error) {
        showNotification(error.message || 'Failed to create order', 'error');
      }
    }
  }, [isModal, createOrder, setOrderId, setCart, setNoteText, setSavedNote, updateDeliveryCharge, showNotification]);

  // Handle medicine selection
  const handleMedicineSelect = useCallback(async (medicine) => {
    if (!orderId) {
      showNotification('Please select a service provider first', 'error');
      return;
    }
    await addMedicineToCart(medicine, showNotification);
    // Clear medicines list, search text, and hide dropdown after selection
    // This gives a clean slate for the next search
    clearMedicineResults();
        setMedicineSearchText('');
        setShowMedicineDropdown(false);
  }, [orderId, addMedicineToCart, showNotification, clearMedicineResults, setMedicineSearchText]);
  
  // Handle save order
  const handleSaveOrder = useCallback(async () => {
    // Prevent saving if status is "initiated"
    if (orderStatus && String(orderStatus).toLowerCase().trim() === 'initiated') {
      showNotification('Cannot save order with "initiated" status', 'error');
      return;
    }
  
    const success = await saveOrder(cart, batchUpdateCartItems, showNotification);
    if (success) {
      if (isModal && onClose) {
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        setTimeout(() => {
          navigate(`/subscriber-wallet/subscriber/${id}`);
        }, 500);
      }
    }
  }, [saveOrder, cart, batchUpdateCartItems, showNotification, isModal, onClose, navigate, id, orderStatus]);
  
  // Handle submit order
  const handleSubmitOrder = useCallback(async () => {
    const success = await submitOrder(cart, batchUpdateCartItems, showNotification);
    if (success) {
      if (isModal && onClose) {
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        setTimeout(() => {
          window.location.href = `/subscriber-wallet/subscriber/${id}`;
        }, 2000);
      }
    }
  }, [submitOrder, cart, batchUpdateCartItems, showNotification, isModal, onClose, id]);
  
  const { generatingLink, paymentLink, generateLink } = useBookingPaymentLink(currentRole);

  // Runs the existing submit chain (batch-update -> save -> submit; an already-initiated order
  // stays initiated) so the link is priced on the cart as shown, then generates the link.
  // Only submit's success toast is silenced. Unlike Create Transaction this neither closes the
  // modal nor redirects, so ops can copy or resend the link.
  const handleGeneratePaymentLink = useCallback(() => generateLink(
    orderId,
    () => submitOrder(cart, batchUpdateCartItems, (message, type) => {
      if (type === 'error') showNotification(message, type);
    }),
    showNotification
  ), [generateLink, orderId, submitOrder, cart, batchUpdateCartItems, showNotification]);

  // Resend never re-submits: the backend re-texts the same link when the amount is unchanged.
  const handleResendPaymentLink = useCallback(
    () => generateLink(orderId, null, showNotification),
    [generateLink, orderId, showNotification]
  );

  // Handle update note
  const handleUpdateNote = useCallback(async () => {
    const success = await updateNote(showNotification);
    if (success) {
      setShowNoteModal(false);
    }
  }, [updateNote, showNotification]);
  
  // Handle cancel order
  const handleCancelOrder = useCallback(async () => {
    const success = await cancelOrderRequest(cancellationReason, showNotification);
    if (success) {
      setShowCancelModal(false);
      setCancellationReason('');
      if (isModal && onClose) {
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        setTimeout(() => {
          navigate(`/subscriber-wallet/subscriber/${id}`);
        }, 1000);
      }
    }
  }, [cancelOrderRequest, cancellationReason, showNotification, isModal, onClose, navigate, id]);
  
  // Handle mark all out of stock
  const handleMarkAllOutOfStock = useCallback(async () => {
    const allOutOfStock = cart.map(item => ({ ...item, outOfStock: true }));
    const success = await markAllOutOfStock(refreshCart, showNotification);
    if (success) {
      setCart(allOutOfStock);
      if (isModal && onClose) {
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1000);
      }
    }
  }, [cart, markAllOutOfStock, refreshCart, showNotification, isModal, onClose, setCart]);
  
  // A paid order is frozen server-side (save.py rejects everything but the note), so the
  // address selector is read-only there rather than offering a pick that cannot be saved.
  //
  // Two independent gates, both needed:
  //
  // `is_payment_locked` is the server's OWN freeze flag and the only thing save.py, update.py,
  // out_of_stock.py and the item router actually check. Mirroring it keeps this honest.
  //
  // The status check is an ALLOWLIST, not a denylist. update_status.py validates no
  // transitions at all — any status can be written over any other — so an order can reach
  // `processed` without ever passing `ordered`, which is the only status that sets the lock
  // flag. A denylist would leave such an order editable; an allowlist leaves anything it does
  // not recognise, including statuses added later, locked.
  //
  // `initiated` is deliberately IN the allowlist. It is where every order lands the moment
  // Create Transaction runs (submit.py), and where a subscriber-reserved order starts, so the
  // old denylist meant a delivery address could never be corrected after the first submit —
  // which is the complaint this came from. Editing it is safe: the pick is applied by Create
  // Transaction, which accepts an `initiated` order and leaves it `initiated`.
  const orderStatusKey = String(orderStatus || '').toLowerCase().trim();
  const isOrderLocked = isPaymentLocked
    || (Boolean(orderStatusKey) && !['draft', 'saved', 'initiated'].includes(orderStatusKey));

  // Validate required data on mount
  useEffect(() => {
    if (!id) {
      setError('Subscriber ID is required');
      return;
    }
    
    if (!currentRole) {
      setError('Invalid role access');
      return;
    }
  }, [id, currentRole]);
  
  // Update orderId in cart hook when it changes
  useEffect(() => {
    // This is handled by the cart hook internally
  }, [orderId]);
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <FaExclamationCircle className="w-16 h-16 mx-auto" />
          </div>
          <p className="text-xl font-semibold text-gray-900 mb-2">Error</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/subscriber-wallet/home')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50'}>
      <div className={isModal ? '' : 'max-w-6xl mx-auto px-4 py-8'}>
        {/* Header - Only show if not in modal */}
        {!isModal && (
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/subscriber-wallet/home';
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
              >
                ← Back to Home
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = `/subscriber-wallet/subscriber/${id}`;
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
              >
                ← Back
              </button>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Manual Transaction - Medicine</h1>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Service Type - Fixed to Medicine */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value="Medicine"
                disabled
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Service Provider */}
            <ProviderSelector
              providers={medicineProviders}
              filteredProviders={filteredMedicineProviders}
              loading={loadingMedicineProviders}
              error={providerError}
              providerSearchText={providerSearchText}
              setProviderSearchText={setProviderSearchText}
              showProviderDropdown={showProviderDropdown}
              setShowProviderDropdown={setShowProviderDropdown}
              selectedProviderId={medicineServiceProvider}
              onProviderSelect={handleProviderSelect}
            />

            {/* Medicine Search */}
            <MedicineSearch
              medicines={filteredMedicines}
              loading={loadingMedicines}
              error={medicineError}
              searchText={medicineSearchText}
              setSearchText={setMedicineSearchText}
              showDropdown={showMedicineDropdown}
              setShowDropdown={setShowMedicineDropdown}
              onMedicineSelect={handleMedicineSelect}
              onEnterPress={triggerMedicineSearch}
              hasSearched={medicineHasSearched}
            />

            {/* Delivery Address — ops pick one of the subscriber's addresses or add one. The pick
                is staged locally and travels with Save / Create Transaction, like every other
                edit on this page. Locked once the order is paid for. */}
            <DeliveryAddressSelector
              role={currentRole}
              applicationId={id}
              selectedAddressId={selectedAddressId}
              savedAddressId={orderAddressId}
              hasOrder={Boolean(orderId)}
              onSelect={setSelectedAddressId}
              onNotify={showNotification}
              disabled={isOrderLocked}
            />

            {/* Prescription (read-only) — the files the subscriber attached to this
                order. Gated like the address block: shown once there is an order. */}
            {orderId && <PrescriptionAttachments documents={prescriptions} />}

            {orderId && ['saved', 'initiated'].includes(String(orderStatus || '').toLowerCase()) && (
              <OrderNoteCard note={savedNote} status={orderStatus} onEdit={() => setShowNoteModal(true)} />
            )}

            {/* Cart */}
            {cart.length > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cart</h3>
                <div className="space-y-4">
                  {cart.map((item) => (
                    <MedicineCartItem
                      key={item.itemId || item.medicineId || `item-${Math.random()}`}
                      item={item}
                      onRemove={(id) => removeFromCart(id, showNotification)}
                      onUpdateMRP={updateCartMRP}
                      onUpdateAyushPayPrice={updateCartAyushPayPrice}
                      onUpdateQuantity={updateCartQuantity}
                      onToggleOutOfStock={toggleOutOfStock}
                    />
                  ))}
                </div>
                <CartSummary
                  cart={cart}
                  orderDeliveryCharge={orderDeliveryCharge}
                  handlingPackagingCharge={handlingPackagingCharge}
                  platformCharge={platformCharge}
                  cartCashback={cartCashback}
                  onUpdateDeliveryCharge={updateDeliveryCharge}
                  onUpdateHandlingPackagingCharge={updateHandlingPackagingCharge}
                  onUpdatePlatformCharge={updatePlatformCharge}
                  orderDeliveryChargeError={orderDeliveryChargeError}
                  handlingPackagingChargeError={handlingPackagingChargeError}
                  platformChargeError={platformChargeError}
                />
              </div>
            )}

            {/* Action Buttons */}
            <OrderActions
              orderId={orderId}
              orderStatus={orderStatus}
              cart={cart}
              savingOrder={savingOrder}
              submittingOrder={submittingOrder}
              markingAllOutOfStock={markingAllOutOfStock}
              hasChargeError={Boolean(orderDeliveryChargeError || handlingPackagingChargeError || platformChargeError)
                || cart.some(item => Number(item.mrp) < 0 || Number(item.ayushpayPrice) < 0)}
              isModal={isModal}
              onMarkAllOutOfStock={handleMarkAllOutOfStock}
              onShowNoteModal={() => setShowNoteModal(true)}
              onShowCancelModal={() => setShowCancelModal(true)}
              onCancel={() => {
                if (isModal && onClose) {
                  onClose();
                } else {
                  navigate(`/subscriber-wallet/subscriber/${id}`);
                }
              }}
              onSave={handleSaveOrder}
              onSubmit={handleSubmitOrder}
              onGeneratePaymentLink={handleGeneratePaymentLink}
              generatingPaymentLink={generatingLink}
              isOrderLocked={isOrderLocked}
              isOrderPaid={isPaid}
            />
            <PaymentLinkCard
              link={isPaid ? null : paymentLink}
              onResend={handleResendPaymentLink}
              resending={generatingLink}
            />
          </div>
        </div>

        {/* Note Modal */}
        <OrderNoteModal
          mounted={mounted}
          show={showNoteModal}
          noteText={noteText}
          setNoteText={setNoteText}
          updatingNote={updatingNote}
          onClose={() => {
            setNoteText(savedNote);
            setShowNoteModal(false);
          }}
          onUpdate={handleUpdateNote}
        />

        {/* Cancel Order Modal */}
        <CancelOrderModal
          mounted={mounted}
          show={showCancelModal}
          cancellationReason={cancellationReason}
          setCancellationReason={setCancellationReason}
          cancellingOrder={cancellingOrder}
          onClose={() => {
            setShowCancelModal(false);
            setCancellationReason('');
          }}
          onCancel={handleCancelOrder}
        />

        {/* Notification Toast */}
        <NotificationToast
          notification={notification}
          onClose={() => setNotification(null)}
          mounted={mounted}
        />
      </div>
    </div>
  );
};

export default MedicineTransaction;
